import React from 'react'
import { Avatar, Box } from '@mui/material'
import { motion } from 'framer-motion'
import Image from 'next/image'
import { formlessColors } from '@zuto/theme/src/mui/Colors'
import type { InterstitialStage } from '../../../../../types/ui'

const MotionBox = motion(Box)

type LogoHandoverProps = {
  stage: InterstitialStage
}

const LogoHandover: React.FC<LogoHandoverProps> = ({ stage }) => {
  const getLeftScale = (s: InterstitialStage) => {
    if (s === 'intro') return 1
    if (s === 'transition') return 0.8
    return 0.65
  }

  const getRightScale = (s: InterstitialStage) => {
    if (s === 'intro') return 1
    if (s === 'transition') return 1.05
    return 1.15
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: '128px',
        marginBottom: { xs: 7, md: 10 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Left logo (partner / ClearScore) */}
      <MotionBox
        initial={{ x: 0, scale: 1, opacity: 1 }}
        animate={{
          x: stage === 'intro' ? 0 : stage === 'transition' ? -60 : -80,
          scale: getLeftScale(stage),
          opacity: 1
        }}
        transition={{ duration: 0.8, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          zIndex: stage === 'intro' ? 2 : 1
        }}
      >
        <Avatar
          sx={{
            width: '64px',
            height: '64px',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(15,23,42,0.2)',
            backgroundColor: 'common.white',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Image
            src="https://cdn.zuto.cloud/assets/images/logos/clearscore-vector.png"
            width={50}
            height={50}
            alt="Clearscore Vector"
            style={{
              width: '50px',
              height: '50px',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </Avatar>
      </MotionBox>

      {/* Animated arrow */}
      <MotionBox
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{
          opacity: stage === 'transition' || stage === 'complete' ? 1 : 0,
          scale: stage === 'transition' || stage === 'complete' ? 1 : 0.5
        }}
        transition={{ duration: 0.4 }}
        sx={{ position: 'absolute', zIndex: 3 }}
      >
        <MotionBox
          animate={{ x: [0, 10, 0] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        >
          <Box
            sx={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: formlessColors.brandYellow,
              fontSize: '32px'
            }}
          >
            ➜
          </Box>
        </MotionBox>
      </MotionBox>

      {/* Right logo (Zuto) */}
      <MotionBox
        initial={{ x: 0, opacity: 0.4, scale: 1 }}
        animate={{
          x: stage === 'intro' ? 0 : stage === 'transition' ? 60 : 80,
          opacity: stage === 'intro' ? 0.4 : stage === 'complete' ? 1 : 0.7,
          scale: getRightScale(stage)
        }}
        transition={{ duration: 0.8, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          zIndex: 1
        }}
      >
        <Avatar
          sx={{
            width: '64px',
            height: '64px',
            borderRadius: '12px',
            backgroundColor: 'common.white',
            boxShadow: '0 10px 25px rgba(15,23,42,0.2)',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Image
            src="https://cdn.zuto.cloud/assets/images/logos/z-zuto-logo.png"
            width={50}
            height={50}
            alt="Zuto Logo Vector"
            style={{
              width: '50px',
              height: '50px',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </Avatar>
      </MotionBox>

      {/* Connecting line */}
      {(stage === 'transition' || stage === 'complete') && (
        <MotionBox
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 160, opacity: 0.3 }}
          transition={{ duration: 0.5 }}
          sx={{
            position: 'absolute',
            height: '2px',
            borderRadius: '999px',
            background: formlessColors.handoverConnectorGradient
          }}
        />
      )}
    </Box>
  )
}

export default LogoHandover
